import { Copy, RefreshCw } from 'lucide-react';
import { Logo } from '../logo/logo';
import { Button } from '../ui/button';
import { useRouteError } from 'react-router-dom';
import { useEffect, useState } from 'react';

export const MainErrorFallback = () => {
  const supportEmail = 'info@liquidbudget.com';
  const [isReloading, setIsReloading] = useState(false);

  let error: unknown = null;
  let isChunkError = false;

  try {
    error = useRouteError();

    // Check if it's a chunk loading error
    if (error instanceof Error) {
      isChunkError =
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Failed to import');
    }
  } catch {
    // Not in router context, that's okay
  }

  useEffect(() => {
    // Auto-reload for chunk errors after a short delay
    if (isChunkError && !isReloading) {
      const timer = setTimeout(() => {
        setIsReloading(true);
        window.location.reload();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isChunkError, isReloading]);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const handleReload = () => {
    setIsReloading(true);
    window.location.reload();
  };

  // Show special UI for chunk errors
  if (isChunkError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg">
          {/* Logo */}
          <div className="flex justify-center text-2xl">
            <Logo textSize="text-4xl" iconSize="size-12" />
          </div>

          {/* Update Message */}
          <div className="text-center space-y-2">
            <div className="mb-4 flex items-center justify-center">
              <div className="rounded-full bg-blue-100 p-3">
                <RefreshCw className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">Update Available</h1>
            <p className="text-gray-500">
              We've just updated Liquid Budget with new features and improvements. The page will automatically reload in
              a moment.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button onClick={handleReload} disabled={isReloading} className="w-full">
              {isReloading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Reloading...
                </>
              ) : (
                'Reload Now'
              )}
            </Button>
            <Button onClick={() => window.location.assign(window.location.origin)} variant="outline" className="w-full">
              Go to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show normal error UI for other errors
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg">
        {/* Logo */}
        <div className="flex justify-center text-2xl">
          <Logo textSize="text-4xl" iconSize="size-12" />
        </div>

        {/* Error Message */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold text-gray-900">Something went wrong :(</h1>
          <p className="text-gray-500">We apologize for the inconvenience. Our team has been notified.</p>
        </div>

        {/* Support Options */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-gray-700 text-center">Need assistance? We're here to help:</p>

          {/* Email */}
          <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
            <span className="text-sm text-gray-600 font-medium">{supportEmail}</span>
            <button
              onClick={copyEmail}
              className="ml-2 rounded-md p-2 hover:bg-white transition-colors duration-200"
              title="Copy email address"
            >
              <Copy className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Reddit */}
          <a
            href="https://reddit.com/r/liquidbudget"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center px-4 py-3 w-full text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Join our Reddit Community
          </a>
        </div>

        {/* Return Home Button */}
        <div className="mt-8 text-center">
          <Button onClick={() => window.location.assign(window.location.origin)} variant="outline" className="w-full">
            Return to Homepage
          </Button>
        </div>
      </div>
    </div>
  );
};
